<template>
  <div>
    <admin-wrapper
      :straight-title="additionalModuleName"
      :has-buttons="false"
      :inputs="inputs"
      :moduleEnabler="moduleEnabler"
      @input="editSingleSettingHandler"
    >
      <template #layers.input>
        <v-row class="py-3">
          <v-col cols="12">
            <draggable-lists-mobile-layers
              schema_scope="cache"
              scope="core"
              :scope-items="computedScopeItems"
              leftTitle="admin.cacheLayers"
              :allowedLayersTypes="['features_layer']"
              :layers-dots-items="[{ name: 'setCacheInterval' }, { name: 'startCache' }]"
              :init-promises="initPromises"
              only-geometric-layers
              @setCacheInterval="setCacheInterval"
              @startCache="startCache"
            >
              <template #appendName="data">
                <template
                  v-if="
                    data.level === 2 &&
                      (cacheLayersMetadata[data.id] ? cacheLayersMetadata[data.id].last_cache_datetime : false)
                  "
                >
                  <v-col class="ml-2" cols="auto">
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span v-on="on">
                          <v-icon color="success">
                            mdi-check
                          </v-icon>
                        </span>
                      </template>
                      {{
                        $i18n.t('admin.cacheGeneratedDate', {
                          date: cacheLayersMetadata[data.id]
                            ? $dayjs(cacheLayersMetadata[data.id].last_cache_datetime).format('L LT')
                            : '',
                        })
                      }}
                    </v-tooltip>
                  </v-col>
                </template>
                <template
                  v-if="
                    data.level === 2 &&
                      (cacheLayersMetadata[data.id] ? cacheLayersMetadata[data.id].status === 'W trakcie' : false)
                  "
                >
                  <v-col class="ml-2" cols="auto">
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <span v-on="on">
                          <main-loader size="20" width="2" />
                        </span>
                      </template>
                      {{ $i18n.t('admin.cacheGenerating') }}
                    </v-tooltip>
                  </v-col>
                </template>
              </template>
            </draggable-lists-mobile-layers>
          </v-col>
        </v-row>
      </template>
    </admin-wrapper>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      :title="$i18n.t('dialog.title.generateLayerCache')"
      :body="$i18n.t('dialog.body.confirmLayerCacheGenerate')"
      @agree="startCacheHandler"
      :loading="isMainDialogLoading"
      agree-text="dialog.turnOn"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      :inputs="mainInputDialogInputs"
      agree-text="dialog.save"
      dialog-width="40%"
      :title-translation="$i18n.t('dialog.title.setCacheInterval')"
      :loading="isMainInputDialogLoading"
      @agree="setCacheIntervalHandler"
    />
  </div>
</template>
<script>
import { debounce } from 'lodash';
import { call, get } from 'vuex-pathify';
import AdminWrapper from '@/components/AdminWrapper';
import DraggableListsMobileLayers from '@/components/DraggableListsMobileLayers';
import MainDialog from '@/components/MainDialog';
import MainInputDialog from '@/components/MainInputDialog';
import MainLoader from '@/components/MainLoader';
export default {
  name: 'LayersCacheSettings',
  components: { AdminWrapper, DraggableListsMobileLayers, MainDialog, MainInputDialog, MainLoader },
  computed: {
    additionalModuleName: get('admin/additionalModules@CACHE_LAYER_MODULE.verbose_name'),
    intervalSettingValue: get('admin/settingsValues@cache_layer_module_update_cache'),
    isWaterModuleEnabled: get('admin/additionalModules@WATER_DATA.enabled'),
    isZDMModuleEnabled: get('admin/additionalModules@ZDM_DATA.enabled'),
    isSewerModuleEnabled: get('admin/additionalModules@SEWER_DATA.enabled'),
    isWaterSettingEnabled: get('admin/settingsValues@water_data_module_enabled'),
    isSewerSettingEnabled: get('admin/settingsValues@sewer_data_module_enabled'),
    cacheLayersMetadata: get('layers/cacheLayersMetadata'),
    computedScopeItems() {
      return [
        { text: this.$i18n.t('admin.selectPublicLayers'), value: 'core' },
        ...(this.isWaterModuleEnabled && this.isWaterSettingEnabled
          ? [{ text: this.$i18n.t('admin.selectWaterLayers'), value: 'water' }]
          : []),
        ...(this.isSewerModuleEnabled && this.isSewerSettingEnabled
          ? [{ text: this.$i18n.t('admin.selectSewerLayers'), value: 'sewer' }]
          : []),
        ...(this.isZDMModuleEnabled ? [{ text: this.$i18n.t('admin.selectZDMLayers'), value: 'zdm_data' }] : []),
      ];
    },
    inputs() {
      return [
        {
          name: 'cache_layer_module_update_cache',
          align: 'start',
          translationPath: 'admin.defaultCacheInterval',
          subtitle: 'admin.layersCacheIntervalSubtitle',
          rules: [v => v === 0 || Boolean(v) || 'rules.required'],
          allowNegative: false,
          minValue: 1,
          dataType: {
            name: 'integer',
          },
        },
        {
          name: 'layers',
          translationPath: 'admin.cacheLayers',
          subtitle: 'admin.cacheLayersSubtitle',
          isCustom: true,
        },
      ];
    },
    moduleEnabler() {
      return {
        name: 'cache_layer_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    mainInputDialogInputs() {
      return [
        {
          name: 'interval',
          translationPath: 'dialog.cacheInterval',
          subtitle: 'dialog.cacheIntervalSubtitle',
          dataType: {
            name: 'integer',
          },
          cols: '10',
          rules: [v => !!v || 'rules.required'],
          value: this.cacheLayersMetadata[this.currentLayerId]?.interval || this.intervalSettingValue,
          allowNegative: false,
          minValue: 1,
        },
      ];
    },
    initPromises() {
      return [this.getCacheLayersMetadata()];
    },
  },
  data: () => ({
    currentLayerId: null,
    intervalValue: '',
    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
  }),
  methods: {
    syncLayerMbTiles: call('layers/syncLayerMbTiles'),
    editSingleSetting: call('admin/editSingleSetting'),
    editCacheLayerMetadata: call('layers/editCacheLayerMetadata'),
    getCacheLayersMetadata: call('layers/getCacheLayersMetadata'),
    editSingleSettingHandler(value, name) {
      if (name === 'cache_layer_module_update_cache' && value) {
        this.editSingleSetting({
          key: name,
          body: {
            value,
          },
        });
      }
    },
    setCacheInterval(e) {
      this.isMainInputDialogLoaded = true;
      this.isMainInputDialogVisible = true;
      this.currentLayerId = e.id;
    },
    async setCacheIntervalHandler() {
      try {
        this.isMainInputDialogLoading = true;
        const data = {
          payload: {
            interval: this.mainInputDialogInputs[0].value,
          },
          id: this.currentLayerId,
        };
        await this.editCacheLayerMetadata(data);
        this.isMainInputDialogVisible = false;
        this.isMainInputDialogLoading = false;
        this.currentLayerId = null;
      } catch {
        this.isMainInputDialogLoading = false;
      }
    },
    startCache(e) {
      this.isMainDialogLoaded = true;
      this.isMainDialogVisible = true;
      this.currentLayerId = e.id;
    },
    async startCacheHandler() {
      try {
        this.isMainDialogLoading = true;
        await this.syncLayerMbTiles(this.currentLayerId);
        this.isMainDialogVisible = false;
        this.isMainDialogLoading = false;
        this.currentLayerId = null;
      } catch {
        this.isMainDialogLoading = false;
      }
    },
  },
  created() {
    this.editSingleSettingHandler = debounce(this.editSingleSettingHandler, 500);
  },
};
</script>
